import { useEffect, useState } from "react";
import { Button, Spin, message } from "antd";
import { CheckCircleFilled, ExclamationOutlined } from "@ant-design/icons";
import YextApi, { YextListing, yextErrorMessage } from "../../api/YextApi";
import "./ListingsListingsTab.scss";

interface Props {
  locationId?: number;
}

// Maps a raw Yext listing status to a display chip (reuses the existing chip styles).
function statusChip(status: string): { cls: string; label: string } {
  switch (status) {
    case "LIVE":
      return { cls: "verified", label: "Live" };
    case "WAITING_ON_CUSTOMER":
      return { cls: "warning", label: "Action needed" };
    case "WAITING_ON_YEXT":
    case "WAITING_ON_PUBLISHER":
      return { cls: "warning", label: "Processing" };
    case "OPTED_OUT":
      return { cls: "danger", label: "Not published" };
    default:
      return { cls: "danger", label: "Unavailable" };
  }
}

export function ListingsListingsTab({ locationId }: Props) {
  const [listings, setListings] = useState<YextListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  async function load() {
    if (!locationId) return;
    setLoading(true);
    try {
      const { data } = await YextApi.getListings(locationId);
      setListings(data);
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to load listings."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  async function publish() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setPublishing(true);
    try {
      const { data } = await YextApi.publish(locationId);
      message.success(`Publishing to ${data.publisherCount} directories — statuses will update as they sync.`);
      await load();
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to publish."));
    } finally {
      setPublishing(false);
    }
  }

  const liveCount = listings.filter((l) => l.status === "LIVE").length;

  return (
    <div className="listings-tab-listings">
      <div className="lll-status-bar">
        <div className="lll-status-summary">
          <span className="lll-status-label">Listing Status</span>
          {listings.length > 0 && (
            <span className="lll-issues-pill">
              <span className="lll-issues-dot">
                <CheckCircleFilled />
              </span>
              <span>
                {liveCount} of {listings.length} live
              </span>
            </span>
          )}
        </div>
        <Button type="primary" className="lll-fix-all-btn" onClick={publish} loading={publishing}>
          Publish
        </Button>
      </div>

      {loading ? (
        <div className="lll-empty">
          <Spin />
        </div>
      ) : listings.length === 0 ? (
        <div className="lll-empty">Not published to any directories yet. Click Publish to go live.</div>
      ) : (
        <div className="lll-table">
          <div className="lll-thead">
            <div className="lll-th lll-col-publisher">Publisher</div>
            <div className="lll-th lll-col-status">Status</div>
          </div>
          <div className="lll-tbody">
            {listings.map((row) => {
              const chip = statusChip(row.status);
              return (
                <div className="lll-row" key={row.publisherId}>
                  <div className="lll-td lll-col-publisher">
                    <div className="lll-publisher-text">
                      <div className="lll-publisher-name">{row.publisherName}</div>
                      {row.listingUrl && (
                        <a
                          className="lll-view-listing-link"
                          href={row.listingUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View Listing
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="lll-td lll-col-status">
                    <span className={`lll-status-chip ${chip.cls}`} title={row.message || undefined}>
                      {chip.cls !== "verified" && (
                        <span className="lll-chip-icon">
                          <ExclamationOutlined />
                        </span>
                      )}
                      <span>{chip.label}</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
